import React, { useCallback, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Space, Button, Tooltip, Drawer } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { topMenuConfig } from '../config/menuConfig';
import { useAuth } from '../context/AuthContext';
import { getUserDisplayName, logoutSession, getLoginUrl } from '../api/auth';
import { useNarrowScreen } from '../hooks/useNarrowScreen';

const { Header, Content, Sider } = AntLayout;

const SIDER_COLLAPSED_KEY = 'kac-sider-collapsed';
const SIDER_WIDTH = 220;

function readSiderCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDER_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { session } = useAuth();
  const narrow = useNarrowScreen();
  const [siderCollapsed, setSiderCollapsed] = useState(readSiderCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleSider = useCallback(() => {
    setSiderCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDER_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const topMenuItems = topMenuConfig.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
  }));

  const firstSeg = pathname.split('/').filter(Boolean)[0] || 'ledger';
  const currentTopKey = '/' + firstSeg;
  const currentConfig = topMenuConfig.find((c) => c.key === currentTopKey);
  const sideItems =
    currentConfig?.children?.map((child) => ({
      key: child.key,
      label: child.label,
    })) ?? [];
  const hasSideMenu = sideItems.length > 0;

  const drawerItems: MenuProps['items'] = topMenuConfig.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
    children: item.children?.map((child) => ({
      key: child.key,
      label: child.label,
    })),
  }));

  const handleLogout = async () => {
    const loginUrl = getLoginUrl();
    try {
      await logoutSession(loginUrl);
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = loginUrl;
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const selectedSideKey =
    pathname.startsWith('/ledger/') && pathname !== '/ledger'
      ? '/ledger/demo'
      : pathname;

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          background: '#001529',
          paddingInline: narrow ? 12 : 16,
        }}
      >
        {narrow ? (
          <Button
            type="text"
            aria-label="打开菜单"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            style={{ color: '#fff', marginRight: 8, flexShrink: 0 }}
          />
        ) : (
          hasSideMenu && (
            <Tooltip title={siderCollapsed ? '展开菜单' : '收起菜单'}>
              <Button
                type="text"
                aria-label={siderCollapsed ? '展开菜单' : '收起菜单'}
                icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={toggleSider}
                style={{ color: '#fff', marginRight: 8, flexShrink: 0 }}
              />
            </Tooltip>
          )
        )}
        <div style={{ color: '#fff', marginRight: narrow ? 8 : 24, fontWeight: 600, flexShrink: 0 }}>
          {narrow ? 'Keepfly' : 'Keepfly 财务中心'}
        </div>
        {!narrow && (
          <Menu
            theme="dark"
            mode="horizontal"
            selectedKeys={[currentTopKey]}
            items={topMenuItems}
            onClick={({ key }) => {
              const config = topMenuConfig.find((c) => c.key === key);
              const firstChild = config?.children?.[0]?.key;
              navigate(firstChild || key);
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
        )}
        {narrow && <div style={{ flex: 1 }} />}
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
          <Space style={{ color: '#fff', cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}>
            <Avatar size="small" icon={<UserOutlined />} />
            {!narrow && <span>{getUserDisplayName(session)}</span>}
          </Space>
        </Dropdown>
      </Header>
      <AntLayout>
        {!narrow && hasSideMenu && (
          <Sider
            width={SIDER_WIDTH}
            collapsedWidth={0}
            collapsed={siderCollapsed}
            collapsible
            trigger={null}
            style={{ background: '#fff', overflow: 'hidden' }}
          >
            <Menu
              mode="inline"
              selectedKeys={[selectedSideKey]}
              items={sideItems}
              onClick={({ key }) => navigate(key)}
              style={{ height: '100%', borderRight: 0 }}
            />
          </Sider>
        )}
        <Content style={{ padding: narrow ? 12 : 24, background: '#f0f2f5', minWidth: 0 }}>
          <Outlet />
        </Content>
      </AntLayout>
      <Drawer
        title="菜单"
        placement="left"
        open={narrow && drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={280}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedSideKey]}
          defaultOpenKeys={[currentTopKey]}
          items={drawerItems}
          onClick={({ key }) => {
            const config = topMenuConfig.find((c) => c.key === key);
            if (config?.children?.[0]) {
              navigate(config.children[0].key);
            } else {
              navigate(key);
            }
            setDrawerOpen(false);
          }}
        />
        <Button
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          style={{ marginTop: 16, width: '100%', textAlign: 'left' }}
        >
          退出登录
        </Button>
      </Drawer>
    </AntLayout>
  );
};

export default Layout;
